import { Button } from "./ui/button";
import { useState } from "react";
import ResponsiveModal from "./ResponsiveModal";
import { useTranslation } from "react-i18next";
import mealPlaceholder from "../assets/meal-placeholder.png";
import type {
  IngredientType,
  ItemType,
  ItemWithIngredientsType,
  IngredientGroupType,
} from "@/types/item";
import { Minus, Plus, Trash } from "lucide-react";

interface ItemModalProps {
  item: ItemType;
  open: boolean;
  onClose: () => void;
  ctaText: string;
  onCtaClick: () => void;
  selectedIngredients?: IngredientType[];
  selectIngredient: (ingredient: IngredientType) => void;
  unselectIngredient: (ingredient: IngredientType) => void;
  ingredientGroups?: IngredientGroupType[];
  quantity?: number;
  onQuantityChange?: (quantity: number) => void;
}

export default function ItemModal({
  item,
  ingredientGroups,
  selectedIngredients,
  selectIngredient,
  unselectIngredient,
  ctaText,
  onCtaClick,
  open,
  onClose,
  quantity = 1,
  onQuantityChange,
}: ItemModalProps) {
  const { i18n, t } = useTranslation();

  const renderContent = () => {
    return (
      <div
        dir={i18n.dir() === "rtl" ? "rtl" : "ltr"}
        className="overflow-y-auto max-h-[75vh]"
      >
        {/* Image */}
        <div className="relative">
          <img
            src={item.image || mealPlaceholder}
            className="aspect-[3/2] w-full h-auto object-cover"
          />
        </div>
        {/* CONTENT */}
        <div className="p-4 flex flex-col gap-6">
          {/* Title */}
          <div className="flex justify-between items-center">
            <h1 className="text-2xl font-bold">{item.name}</h1>
            <p className="text-primary font-semibold text-xl">₪{item.price}</p>
          </div>
          {/* Description */}
          <p>{item.description}</p>
          {/* Restaurant */}
          {/* {sourceImageUrl && sourceName && sourceDescription && (
            <div className="flex flex-col gap-2">
              <p className="text-sm text-gray-500">ORDER FROM</p>
              <div
                className="border h-24 rounded-lg border-gray-300 flex gap-2 items-center cursor-pointer hover:bg-gray-100 transition-all duration-300"
                onClick={onSourceClick}
              >
                <img
                  src={sourceImageUrl}
                  className="h-full w-32 object-cover rounded-s-lg"
                />
                <div className="flex flex-col justify-center">
                  <p>{sourceName}</p>
                  <p className="text-sm text-gray-500">{sourceDescription}</p>
                </div>
                <ChevronRight className="w-8 h-8 ms-auto me-2 text-gray-500" />
              </div>
            </div>
          )} */}

          {/* Ingredients */}
          {ingredientGroups &&
            selectedIngredients &&
            ingredientGroups?.map((ingredientGroup) => (
              <div className="space-y-2">
                <p>{ingredientGroup.name}:</p>
                {!ingredientGroup.isMulti
                  ? ingredientGroup.ingredients.map((ingredient) => (
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={ingredientGroup.name}
                          value={ingredient.name}
                          checked={selectedIngredients.some(
                            (selectedIngredient) =>
                              selectedIngredient.id === ingredient.id
                          )}
                          className="h-6 w-6 accent-primary focus:outline-none"
                          onChange={() => {
                            selectIngredient(ingredient);
                          }}
                        />
                        <label className="flex-1">{ingredient.name}</label>
                        <p className="text-sm text-gray-500">
                          +₪{ingredient.price}
                        </p>
                      </div>
                    ))
                  : ingredientGroup.ingredients.map((ingredient) => (
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          name={ingredient.name}
                          value={ingredient.name}
                          checked={selectedIngredients.some(
                            (selectedIngredient) =>
                              selectedIngredient.id === ingredient.id
                          )}
                          className="h-6 w-6 accent-primary focus:outline-none"
                          onChange={(e) => {
                            if (e.target.checked) {
                              selectIngredient(ingredient);
                            } else {
                              unselectIngredient(ingredient);
                            }
                          }}
                        />
                        <label className="flex-1">{ingredient.name}</label>
                        <p className="text-sm text-gray-500">
                          +₪{ingredient.price}
                        </p>
                      </div>
                    ))}
              </div>
            ))}

          <div className="flex gap-1">
            {/* Order Button */}
            <Button size="lg" className="flex-1" onClick={onCtaClick}>
              {ctaText}
            </Button>

            {quantity && onQuantityChange && (
              <div className="flex w-32 items-center gap-2 bg-[#F7F7F7] rounded-full border border-gray-200 shadow-sm">
                <button
                  className="bg-white rounded-full h-10 flex-1 flex justify-center items-center hover:text-primary cursor-pointer disabled:text-gray-500 disabled:opacity-80"
                  onClick={() => {
                    onQuantityChange?.(quantity - 1);
                  }}
                  disabled={quantity === 1}
                >
                  <Minus className="size-3" strokeWidth={4} />
                </button>
                <p className="text-sm font-semibold flex-1 text-center">
                  {quantity}&nbsp;×
                </p>
                <button
                  className="bg-white rounded-full h-10 flex-1 flex justify-center items-center hover:text-primary cursor-pointer"
                  onClick={() => {
                    onQuantityChange?.(quantity + 1);
                  }}
                >
                  <Plus className="size-3" strokeWidth={4} />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ResponsiveModal
      open={open}
      onClose={() => {
        onClose();
      }}
      renderContent={renderContent}
    />
  );
}
